import { h } from 'preact';
import Store from 'store/store';
import { joinClasses, parseDate } from 'utils/utils';
import { getShowtimesForCMSPreview } from 'components/editor/movies/utils';
import * as jsonPointer from 'utils/jsonPointer';
import formatDateTime from 'utils/formatDateTime';
import { distanceFrom, geoFromIP } from 'utils/geo';

// eslint-disable-next-line no-unused-vars
import WatchPageEditor from 'components/editor/movies/watch/watch';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import IconPlus from 'components/icons/plus';
import IconShowtimesSource from 'components/icons/showtimesSource';
import Button from 'components/button/button';
import Toggle from 'components/toggle/toggle';

const quickFilterOptions = [
	'2D',
	'3D',
	'35mm',
	'70mm',
	'IMAX',
	'IMAX 3D',
	'IMAX 70mm',
	'RealD',
	'ScreenX',
	'ScreenX RealD',
	'Dolby',
	'D-Box',
	'4DX',
];

/**
 * A mixin that adds showtimes-specific behavior to a WatchPageEditor.
 * Used in place of a subclass to avoid issues with multiple inheritance.
 * @template {typeof WatchPageEditor} T
 * @param {T} Base 
 * @returns {T}
 */
export const withShowtimesBehavior = (Base) => class extends Base {
	type = 'showtimes';

	constructor(props) {
		super(props);

		this.setState({
			geo: { lat: 34.0549, lon: -118.2426, city: 'Los Angeles', countryCode: 'us' },
			showtimes: [],
			showtimesDates: [],
		});

		this.toggleQuickFilterOption = this.toggleQuickFilterOption.bind(this);
		this.resetQuickFilters = this.resetQuickFilters.bind(this);
		this.renderTheater = this.renderTheater.bind(this);
	}


	componentDidMount() {
		super.componentDidMount?.();
		this.updateShowtimes();
	}

	componentWillReceiveProps(nextProps) {
		super.componentWillReceiveProps?.(nextProps);
		if (nextProps.data?.content?.showtimesId !== this.state.loadedShowtimesId) {
			this.updateShowtimes(nextProps);
		}
	}

	async updateShowtimes(props = this.props) {
		const showtimesId = props.data?.content?.showtimesId ?? props.data?.base_movie?.content?.showtimesId;
		this.setState({ loadedShowtimesId: showtimesId, showtimesLoading: 'loading' });
		if (!showtimesId) {
			return;
		}
		try {
			const geo = await geoFromIP();
			this.setState({ geo });
		} catch {}
		try {
			const { data, dates } = await getShowtimesForCMSPreview(showtimesId, this.state.geo);
			this.setState({
				showtimesLoading: 'loaded',
				showtimes: data,
				showtimesDates: dates,
			});
		} catch (e) {
			this.setState({ showtimesLoading: 'error' });
		}
	}

	getData() {
		return this.movie?.pages?.showtimes || {};
	}

	renderTopLinks() {
		const homeEntActive = this.movie?.pages?.homeEnt?.active;
		if (!homeEntActive) return <div class={this.s.topLinks} />;

		return (
			<div class={this.s.topLinks}>
				<div class={this.s.topLink}>
					{this.text('$WATCH_AT_HOME', 'Watch at Home')}
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke-linejoin="round" stroke-linecap="round">
						<rect x="2" y="4" width="12" height="9" rx="1" />
						<path d="M5 1L8 4L11 1" />
					</svg>
				</div>
			</div>
		);
	}

	renderFiltersButton() {
		return (
			<span class={this.s.filtersButton}>
				{this.text('$FILTERS', 'Filters')}
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" stroke-linecap="round">
					<circle cx="11.5" cy="5.5" r="2" />
					<circle cx="4.5" cy="10.5" r="2" />
					<path d="M.5 5.5L9 5.5M14 5.5L15.5 5.5M.5 10.5L2 10.5M7 10.5L15.5 10.5" />
				</svg>
			</span>
		);
	}

	renderSearch() {
		return (
			<div class={this.s.search}>
				<div class={this.s.searchWrapper}>
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
						<path d="M14.854 14.146L10.726 10.018C11.521 9.065 12 7.839 12 6.5C12 3.462 9.538 1 6.5 1C3.462 1 1 3.462 1 6.5C1 9.538 3.462 12 6.5 12C7.839 12 9.065 11.521 10.019 10.726L14.147 14.854C14.244 14.951 14.372 15 14.5 15C14.628 15 14.756 14.951 14.854 14.854C15.049 14.658 15.049 14.342 14.854 14.146ZM6.5 11C4.019 11 2 8.981 2 6.5C2 4.019 4.019 2 6.5 2C8.981 2 11 4.019 11 6.5C11 8.981 8.981 11 6.5 11Z" />
					</svg>
					<div class={this.s.city}>{this.state.geo.city}</div>
				</div>
			</div>
		);
	}

	renderMainHeader() {
		const dateTimeCopy = this.state.thundrData?.copy?.baseCopy?.$DATETIME_LOCALE || {};
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const tomorrow = new Date(+today);
		tomorrow.setDate(tomorrow.getDate() + 1);

		let dates = this.state.showtimesDates;
		if (!dates?.length) {
			dates = [formatDateTime(today, 'yyyy-MM-dd')];
		}
		return (
			<div class={this.s.mainHeader}>
				{this.renderSearch()}
				<div class={this.s.dates}>
					{dates.map((date, i) => {
						const parsed = parseDate(date);
						const isToday = +parsed === +today;
						const isTomorrow = +parsed === +tomorrow;
						const label = isToday
							? this.text('$TODAY', 'Today')
							: isTomorrow
								? this.text('$TOMORROW', 'Tomorrow')
								: formatDateTime(parsed, 'ddd', dateTimeCopy);
						return (
							<div class={joinClasses(this.s.date, i === 0 && this.s.active)}>
								<div class={this.s.name}>{label}</div>
								<div class={this.s.details}>{formatDateTime(parsed, 'MMM dd', dateTimeCopy)}</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	renderQuickFilters() {
		const data = this.getData();
		const quickFilters = data.quickFilters;
		const options = Object.entries(quickFilters || {}).map(e => e[1] && e[0]).filter(Boolean);
		if (!options.length) {
			return (
				<SelectableSection class={joinClasses(this.s.quickFilters, this.s.add)} section="/pages/showtimes/quickFilters" {...this.props}>
					<IconPlus /><span>Add quick filters</span>
				</SelectableSection>
			);
		}
		options.sort((a, b) => quickFilterOptions.indexOf(a) - quickFilterOptions.indexOf(b));
		return (
			<SelectableSection class={this.s.quickFilters} section="/pages/showtimes/quickFilters" {...this.props}>
				<div class={this.s.scroller}>
					<div class={this.s.cta}>{data.quickFiltersText || 'Select a format'}</div>
					<div class={joinClasses(this.s.filter, this.s.active)} key="all">ALL</div>
					{options.map((option) => <div class={this.s.filter} key={option}>{option}</div>)}
				</div>
			</SelectableSection>
		);
	}

	renderMainTop() {
		return (
			<div class={this.s.top}>
				{this.renderTopLinks()}
				{this.renderMainHeader()}
			</div>
		);
	}

	renderMainContent() {
		return [
			this.renderFilters(),
			this.renderShowtimes(),
		];
	}

	renderFilters() {
		return (
			<div class={this.s.filters}>
				{this.renderQuickFilters()}
				{this.renderFiltersButton()}
			</div>
		);
	}

	renderShowtimes() {
		if (!this.movie?.showtimesId) {
			return (
				<div class={this.s.setShowtimes} key="set">
					Please set a showtimes source to view showtimes
					<a class={this.s.button} href={`#${this.props.config.basePath}/info`}>
						<IconShowtimesSource />
						Set showtimes source
					</a>
				</div>
			);
		}

		if (this.state.showtimesLoading === 'loading') {
			return (
				<div class={this.s.loading}>
					Loading...
				</div>
			);
		}

		return (
			<div class={this.s.showtimesWrapper}>
				{this.state.showtimes.map(this.renderTheater)}
			</div>
		);
	}

	renderTheater(theater) {
		const address = [
			theater.address?.intl?.street,
			theater.address?.intl?.city,
			// showState && addressPart('state'),
			// showPostcode && addressPart('postcode'),
		].filter(Boolean).join(', ');
		const byFormat = Object.groupBy(theater.screenings, s => s.format);
		const logo = theater?.chain?.logo || 'default.png';
		return (
			<div class={this.s.theater} key={theater.id}>
				<div class={this.s.theaterHeading}>
					<div class={this.s.icon}>
						<img src={`${__SHARED_MEDIA_ROOT__}/images/logos/light/${logo}`} alt="" />
					</div>
					<div class={this.s.name}>{theater.name}</div>
					<div class={this.s.location}>
						<div class={this.s.address}>{address}</div>
						<div class={this.s.distance}>{distanceFrom(this.state.geo, theater, 'mi', 1)}mi</div>
					</div>
				</div>
				<div class={this.s.screenings}>
					{Object.entries(byFormat).map(([format, screenings]) => (
						<div class={this.s.format} key={format}>
							<div class={this.s.formatName}>{format}</div>
							<div class={this.s.formatContent}>
								{screenings.map(scr => <div class={this.s.screening} key={scr.id}>{scr.time}</div>)}
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	toggleQuickFilterOption(e) {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/showtimes/quickFilters/${e.target.value}`, e.target.checked);
	}

	resetQuickFilters() {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/showtimes/quickFilters`, null);
	}

	renderQuickFiltersSettings() {
		const value = this.movie?.pages?.showtimes?.quickFilters;

		return (
			<div class={this.s.quickFilterSettings}>
				<SimpleField
					dark
					label="Quick filters text"
					path={`${this.props.dataRoot}/content/pages/showtimes/quickFiltersText`}
					placeholder="Select a format"
				/>
				{quickFilterOptions.map(option => (
					<Toggle dark checked={value?.[option]} value={option} onInput={this.toggleQuickFilterOption} key={option}>{option}</Toggle>
				))}
				<Button dark danger text class={this.s.remove} disabled={!value} onClick={this.resetQuickFilters} key="remove">Remove quick filters</Button>
			</div>
		);
	}

	getSettingsDetails() {
		switch (this.props.activeElement) {
			case '/pages/showtimes/quickFilters':
				return {
					nav: [{ label: 'Quick filters', path: '/pages/showtimes/quickFilters' }],
					content: this.renderQuickFiltersSettings(),
				};
		}
		return super.getSettingsDetails();
	}
};